import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { CommonService } from 'src/app/shared/common.service';
import * as agentsActions from './agents.actions';
import { catchError, finalize, map, of, switchMap, tap } from 'rxjs';
import { APIResponse } from 'src/app/core/models/api-response';
import { IAgent } from '../models/agents.models';
import { TranslateService } from '@ngx-translate/core';
import { AgentService } from '../services/agent.service';
import { ICatalog } from '../../catalogs/models/catalogs.models';
import { CommissionService } from '../services/commission.service';
import { ICommission } from '../models/commissions.models';

@Injectable()
export class AgentsEffects {
  constructor(
    private actions$: Actions,
    public store: Store<AppState>,
    private agentService: AgentService,
    private commissionService: CommissionService,
    private commonService: CommonService,
    private translateService: TranslateService
  ) {}

  /**
   * ╔════════════════════════════════════════════════════════════════════╗
   * ║                                                                    ║
   * ║                         Agent Effects                              ║
   * ║                                                                    ║
   * ║   All effects related to agent management in the application.      ║
   * ║   Includes loading, adding, updating, and deleting agents.         ║
   * ║                                                                    ║
   * ╚════════════════════════════════════════════════════════════════════╝
   */

  GetAgentsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(agentsActions.GetAgents),
      tap(() => {
        this.store.dispatch(agentsActions.SetAgentLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.agentService.getAgents(action.filter, action.key, action.subsidiary_id).pipe(
          map((res: APIResponse<IAgent>) =>
            agentsActions.GetAgentsSuccess({ agents: res })
          ),
          catchError(() => of(agentsActions.GetAgentsError())),
          finalize(() => {
            this.store.dispatch(
              agentsActions.SetAgentLoader({ isLoading: false })
            );
          })
        )
      )
    )
  );

  GetAgentByIdEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(agentsActions.GetAgentById),
      tap(() => {
        this.store.dispatch(agentsActions.SetAgentLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.agentService.getAgentById(action.id, action.params).pipe(
          map((res: APIResponse<IAgent>) =>
            agentsActions.GetAgentByIdSuccess({ agent: res.object! })
          ),
          catchError(() => of(agentsActions.GetAgentByIdError())),
          finalize(() => {
            this.store.dispatch(
              agentsActions.SetAgentLoader({ isLoading: false })
            );
          })
        )
      )
    )
  );

  AddAgentEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(agentsActions.AddAgent),
      tap(() => {
        this.store.dispatch(agentsActions.SetAgentLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.agentService.addAgent(action.agent).pipe(
          map((res: APIResponse<IAgent>) =>
            agentsActions.AddAgentSuccess({ agent: res.object! })
          ),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant(
                `alerts.add.success`
              ),
              'SUCCESS'
            );
          }),
          catchError(() => of(agentsActions.AddAgentError())),
          finalize(() => {
            this.store.dispatch(
              agentsActions.SetAgentLoader({ isLoading: false })
            );
          })
        )
      )
    )
  );

  UpdateAgentEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(agentsActions.UpdateAgent),
      tap(() => {
        this.store.dispatch(agentsActions.SetAgentLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.agentService.updateAgent(action.id, action.agent).pipe(
          map((res: APIResponse<IAgent>) =>
            agentsActions.UpdateAgentSuccess({ agent: res.object! })
          ),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant(
                `alerts.update.success`
              ),
              'SUCCESS'
            );
          }),
          catchError(() => of(agentsActions.UpdateAgentError())),
          finalize(() => {
            this.store.dispatch(
              agentsActions.SetAgentLoader({ isLoading: false })
            );
          })
        )
      )
    )
  );

  deleteAgentsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(agentsActions.DeleteAgents),
      tap(() => {
        this.store.dispatch(agentsActions.SetAgentLoader({ isLoading: true }));
      }),
      switchMap((action) => {
        return this.agentService.deleteAgent(action.ids).pipe(
          map((res: APIResponse<number>) => agentsActions.DeleteAgentsSuccess({ ids: res.data! })),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant(
                `alerts.delete.success`
              ),
              'SUCCESS'
            );
          }),
          catchError(() => of(agentsActions.DeleteAgentsError())),
          finalize(() => {
            this.store.dispatch(
              agentsActions.SetAgentLoader({ isLoading: false })
            );
          })
        )
      })
    )
  );

  // Get Agents Catalogs
  getAgentsCatalogsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(agentsActions.GetAgentsCatalogs),
      tap(() => {
        this.store.dispatch(agentsActions.SetAgentLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.agentService.getCatalogs(action.page, action.filter, action.key).pipe(
          map((res: APIResponse<ICatalog>) =>
            agentsActions.GetAgentsCatalogsSuccess({ catalogs: res })
          ),
          catchError(() => of(agentsActions.GetAgentsError())),
          finalize(() => {
            this.store.dispatch(
              agentsActions.SetAgentLoader({ isLoading: false })
            );
          })
        )
      )
    )
  );

  /**
   * ╔════════════════════════════════════════════════════════════════════╗
   * ║                                                                    ║
   * ║                      Commission Effects                            ║
   * ║                                                                    ║
   * ║   All effects related to commission management in the application. ║
   * ║   Includes loading, adding, updating, and deleting commissions.    ║
   * ║                                                                    ║
   * ╚════════════════════════════════════════════════════════════════════╝
   */

  // Get Commissions
  getCommissionsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(agentsActions.GetCommissions),
      tap(() => {
        this.store.dispatch(agentsActions.SetAgentLoader({ isLoading: true }));
      }),
      switchMap(() =>
        this.commissionService.getCommissions().pipe(
          map((res: APIResponse<ICommission>) =>
            agentsActions.GetCommissionsSuccess({ commissions: res })
          ),
          catchError(() => of(agentsActions.GetCommissionsError())),
          finalize(() => {
            this.store.dispatch(
              agentsActions.SetAgentLoader({ isLoading: false })
            );
          })
        )
      )
    )
  );

  // Get Commission By Id
  GetCommissionByIdEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(agentsActions.GetCommissionById),
      tap(() => {
        this.store.dispatch(agentsActions.SetAgentLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.commissionService.getCommissionById(action.id).pipe(
          map((res: APIResponse<ICommission>) =>
            agentsActions.GetCommissionByIdSuccess({ commission: res.object! })
          ),
          catchError(() => of(agentsActions.GetCommissionByIdError())),
          finalize(() => {
            this.store.dispatch(
              agentsActions.SetAgentLoader({ isLoading: false })
            );
          })
        )
      )
    )
  );

  // Create Commission
  createCommissionEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(agentsActions.AddCommission),
      tap(() => {
        this.store.dispatch(agentsActions.SetAgentLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.commissionService.addCommission(action.commission).pipe(
          map((res: APIResponse<ICommission>) =>
            agentsActions.AddCommissionSuccess({ commission: res.object! })
          ),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant('alerts.add.success'),
              'SUCCESS'
            );
          }),
          catchError(() => of(agentsActions.AddCommissionError())),
          finalize(() => {
            this.store.dispatch(
              agentsActions.SetAgentLoader({ isLoading: false })
            );
          })
        )
      )
    )
  );

  // Update Commission
  UpdateCommissionEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(agentsActions.UpdateCommission),
      tap(() => {
        this.store.dispatch(agentsActions.SetAgentLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.commissionService.updateCommission(action.id, action.commission).pipe(
          map((res: APIResponse<ICommission>) =>
            agentsActions.UpdateCommissionSuccess({ commission: res.object! })
          ),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant(
                `alerts.update.success`
              ),
              'SUCCESS'
            );
          }),
          catchError(() => of(agentsActions.UpdateAgentError())),
          finalize(() => {
            this.store.dispatch(
              agentsActions.SetAgentLoader({ isLoading: false })
            );
          })
        )
      )
    )
  );

  // Delete Commissions
  deleteCommissionsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(agentsActions.DeleteCommissions),
      tap(() => {
        this.store.dispatch(agentsActions.SetAgentLoader({ isLoading: true }));
      }),
      switchMap((action) => {
        return this.commissionService.deleteCommissions(action.ids).pipe(
          map((res: APIResponse<number>) => agentsActions.DeleteCommissionsSuccess({ ids: res.data! })),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant(
                `alerts.delete.success`
              ),
              'SUCCESS'
            );
          }),
          catchError(() => of(agentsActions.DeleteAgentsError())),
          finalize(() => {
            this.store.dispatch(
              agentsActions.SetAgentLoader({ isLoading: false })
            );
          })
        )
      })
    )
  );
}
